import env from '@config/env';
import { applyAuthorizationHeader } from '@utils/authTokens';
import axios from 'axios';

const PLACE_DETAILS_FIELDS = [
  'name',
  'formatted_address',
  'geometry/location',
  'photos',
  'opening_hours',
  'rating',
  'user_ratings_total',
  'price_level',
  'types',
  'editorial_summary',
].join(',');

const placesClient = axios.create({
  baseURL: '',
  timeout: 15000,
  headers: {
    Accept: 'application/json',
  },
});

placesClient.interceptors.request.use((config) => {
  // APP_TYPE admin → admin token; otherwise → merchant token
  config.headers = applyAuthorizationHeader(config.headers || {});
  return config;
});

const formatPriceLevel = (level) => {
  if (level == null || Number.isNaN(Number(level))) return '';
  return '$'.repeat(Math.min(Math.max(Number(level), 1), 4));
};

const formatCategory = (types = []) => {
  const preferred = types.find(
    (type) => !['point_of_interest', 'establishment', 'premise', 'geocode'].includes(type),
  );
  return (preferred || types[0] || 'establishment').replace(/_/g, ' ');
};

const getClosingTime = (openingHours) => {
  if (!openingHours) return '';

  if (Array.isArray(openingHours.weekday_text) && openingHours.weekday_text.length > 0) {
    const todayIndex = (new Date().getDay() + 6) % 7; // Google list starts Monday
    const todayText = openingHours.weekday_text[todayIndex] || openingHours.weekday_text[0];
    const match = String(todayText).match(/[–-]\s*(.+)$/);
    return match?.[1]?.trim() || '';
  }

  return '';
};

const buildPlacePhotoUrl = (photoReference, maxWidth = 800) => {
  if (!photoReference || !env.GOOGLE_MAP_KEY_SEARCH) return '';

  const params = new URLSearchParams({
    maxwidth: String(maxWidth),
    photo_reference: photoReference,
    key: env.GOOGLE_MAP_KEY_SEARCH,
  });

  // Absolute Google URL so the business detail <img> loads without the webpack /maps proxy.
  return `https://maps.googleapis.com/maps/api/place/photo?${params.toString()}`;
};

const GOOGLE_PLACES_STATUSES = new Set([
  'OK',
  'ZERO_RESULTS',
  'INVALID_REQUEST',
  'OVER_QUERY_LIMIT',
  'REQUEST_DENIED',
  'UNKNOWN_ERROR',
  'NOT_FOUND',
]);

const unwrapPlacesBody = (payload) => {
  const root = payload?.data ?? payload ?? {};
  const nested = root?.data && typeof root.data === 'object' ? root.data : root;
  return { root, nested };
};

const readGooglePlacesStatus = (value) => {
  if (typeof value !== 'string') return '';
  const status = value.trim().toUpperCase();
  return GOOGLE_PLACES_STATUSES.has(status) ? status : '';
};

const readStructuredText = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return String(value.text || value.main_text || '').trim();
};

export const mapPlacePrediction = (prediction) => {
  const place = prediction?.placePrediction || prediction?.place_prediction || prediction;
  const structured =
    place?.structured_formatting || place?.structuredFormatting || place?.structuredFormat || {};
  const name =
    readStructuredText(structured.main_text) ||
    readStructuredText(structured.mainText) ||
    place?.mainText ||
    place?.name ||
    place?.description?.split(',')[0] ||
    place?.text?.text ||
    'Unknown business';
  const address =
    readStructuredText(structured.secondary_text) ||
    readStructuredText(structured.secondaryText) ||
    place?.secondaryText ||
    place?.address ||
    place?.formattedAddress ||
    place?.description ||
    '';
  const placeId = place?.place_id || place?.placeId || place?.id;

  return {
    id: placeId,
    placeId,
    name,
    address,
    category: formatCategory(place?.types),
    description: place?.description || address || name,
    isOpen: true,
    closingTime: '',
    rating: null,
    reviewCount: null,
    priceLevel: '',
    about: '',
    image: '',
    lat: null,
    lng: null,
  };
};

/** Extracts Google-style predictions from backend or Places API payloads. */
export const extractPlacePredictions = (payload) => {
  const { root, nested } = unwrapPlacesBody(payload);

  if (Array.isArray(nested)) return nested;
  if (Array.isArray(nested?.predictions)) return nested.predictions;
  if (Array.isArray(nested?.suggestions)) return nested.suggestions;
  if (Array.isArray(nested?.places)) return nested.places;
  if (Array.isArray(nested?.results)) return nested.results;
  if (Array.isArray(nested?.items)) return nested.items;
  if (Array.isArray(root?.predictions)) return root.predictions;
  if (Array.isArray(root?.suggestions)) return root.suggestions;
  if (Array.isArray(root?.places)) return root.places;
  if (Array.isArray(root?.results)) return root.results;
  if (Array.isArray(payload?.predictions)) return payload.predictions;

  return [];
};

/** Google Places status only — ignore HTTP codes and `{ success: true }` flags. */
export const getPlaceAutocompleteStatus = (payload) => {
  const { root, nested } = unwrapPlacesBody(payload);
  const statusSource = Array.isArray(nested) ? root : nested;
  return readGooglePlacesStatus(statusSource?.status) || readGooglePlacesStatus(root?.status) || '';
};

/** Extracts Google-style place details result from backend or Places API payloads. */
export const extractPlaceDetailsResult = (payload) => {
  const root = payload?.data ?? payload ?? {};
  const nested = root?.data && typeof root.data === 'object' ? root.data : root;

  if (nested?.result && typeof nested.result === 'object') return nested.result;
  if (root?.result && typeof root.result === 'object') return root.result;
  if (payload?.result && typeof payload.result === 'object') return payload.result;

  // Flat place object from merchant API (no Google `result` wrapper).
  const candidate = nested?.place || nested?.details || nested;
  if (
    candidate &&
    typeof candidate === 'object' &&
    !Array.isArray(candidate) &&
    (candidate.place_id || candidate.placeId || candidate.name || candidate.formatted_address)
  ) {
    return candidate;
  }

  return null;
};

/** Extracts the backend-provided cover image URL that sits alongside the Google `result`. */
export const extractPlaceCoverImage = (payload) => {
  const root = payload?.data ?? payload ?? {};
  const nested = root?.data && typeof root.data === 'object' ? root.data : root;

  return nested?.coverImage || root?.coverImage || payload?.coverImage || '';
};

export const mapPlaceDetails = (result = {}, fallback = {}, coverImage = '') => {
  const photoReference = result?.photos?.[0]?.photo_reference;
  const location = result?.geometry?.location;

  return {
    ...fallback,
    id: result.place_id || fallback.placeId || fallback.id,
    placeId: result.place_id || fallback.placeId || fallback.id,
    name: result.name || fallback.name || 'Unknown business',
    address: result.formatted_address || fallback.address || '',
    category: formatCategory(result.types || []),
    isOpen: Boolean(result.opening_hours?.open_now),
    closingTime: getClosingTime(result.opening_hours),
    rating: result.rating ?? fallback.rating ?? null,
    reviewCount: result.user_ratings_total ?? fallback.reviewCount ?? null,
    priceLevel: formatPriceLevel(result.price_level),
    about: result.editorial_summary?.overview || fallback.about || '',
    image: coverImage || buildPlacePhotoUrl(photoReference) || fallback.image || '',
    lat: location?.lat ?? fallback.lat ?? null,
    lng: location?.lng ?? fallback.lng ?? null,
    detailsLoaded: true,
  };
};

const getAddressComponent = (components = [], type, useShortName = false) => {
  const match = components.find((item) => Array.isArray(item.types) && item.types.includes(type));
  if (!match) return '';
  return String((useShortName ? match.short_name : match.long_name) || '').trim();
};

/** Maps Google Geocoding address_components into location form fields. */
export const mapGeocodeAddress = (result = {}) => {
  const components = result.address_components || [];

  const streetNumber = getAddressComponent(components, 'street_number');
  const route = getAddressComponent(components, 'route');
  const streetAddress = [streetNumber, route].filter(Boolean).join(' ').trim();

  return {
    streetAddress: streetAddress || result.formatted_address || '',
    city:
      getAddressComponent(components, 'locality') ||
      getAddressComponent(components, 'postal_town') ||
      getAddressComponent(components, 'sublocality') ||
      getAddressComponent(components, 'administrative_area_level_2'),
    state: getAddressComponent(components, 'administrative_area_level_1'),
    zipCode: getAddressComponent(components, 'postal_code'),
    country: getAddressComponent(components, 'country'),
  };
};

/** Maps merchant/admin reverse-geocode API payloads into location form fields. */
export const mapReverseGeocodeResponse = (payload = {}) => {
  const root = payload?.data ?? payload ?? {};
  const nested = root?.data && typeof root.data === 'object' ? root.data : root;

  const results = nested?.results || root?.results;
  if (Array.isArray(results) && results[0]) {
    return mapGeocodeAddress(results[0]);
  }

  const result = nested?.result || root?.result;
  if (result && typeof result === 'object' && Array.isArray(result.address_components)) {
    return mapGeocodeAddress(result);
  }

  if (nested && typeof nested === 'object' && Array.isArray(nested.address_components)) {
    return mapGeocodeAddress(nested);
  }

  const formatedAddress =
    (nested?.formatedAddress &&
      typeof nested.formatedAddress === 'object' &&
      nested.formatedAddress) ||
    (nested?.formattedAddress &&
      typeof nested.formattedAddress === 'object' &&
      nested.formattedAddress) ||
    (root?.formatedAddress && typeof root.formatedAddress === 'object' && root.formatedAddress) ||
    null;

  const source = formatedAddress || nested || {};

  return {
    streetAddress:
      source.addressLine1 ||
      source.address_line_1 ||
      source.streetAddress ||
      source.address ||
      (typeof nested?.formatted_address === 'string' ? nested.formatted_address : '') ||
      (typeof nested?.formattedAddress === 'string' ? nested.formattedAddress : '') ||
      '',
    city: source.city || '',
    state: source.state || '',
    zipCode: source.zipcode || source.zipCode || source.postalCode || '',
    country: source.country || '',
  };
};

export const placesApi = {
  autocomplete: (input) =>
    placesClient.get('/maps/api/place/autocomplete/json', {
      params: {
        input,
        key: env.GOOGLE_MAP_KEY_SEARCH,
        types: 'establishment',
      },
      hideLoader: true,
      hideErrorToast: true,
    }),

  getDetails: (placeId) =>
    placesClient.get('/maps/api/place/details/json', {
      params: {
        place_id: placeId,
        fields: PLACE_DETAILS_FIELDS,
        key: env.GOOGLE_MAP_KEY_SEARCH,
      },
      hideErrorToast: true,
    }),
};

export default placesApi;
